// src/modules/pricing/pages/PricingPage.js

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../shared/components/layout/Layout';
import { Button, Card } from '../../../shared/components/ui';
import SEO from '../../../shared/components/SEO';

const PricingPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const sharedFeatures = [
        t('pricing.features.menuGenerator'),
        t('pricing.features.recipeCreation'),
        t('pricing.features.shoppingList'),
        t('pricing.features.personalization'),
        t('pricing.features.updates'),
    ];

    const plans = [
        {
            key: 'weekly',
            title: t('pricing.weekly.title'),
            price: t('pricing.weekly.price'),
            period: t('pricing.weekly.period'),
            description: t('pricing.weekly.description'),
            note: t('pricing.weekly.note'),
            badge: t('pricing.weekly.badge', { defaultValue: '' }) || null,
            highlight: false,
            stripeBtnId: "buy_btn_1SRfV4I1TlCT7peU9qSJJWtb",
            stripeBtnKey: "pk_live_51SP7cmI1TlCT7peUfpXils01Bpuh5LMIczy6dwh07c0yoQMPK6Rc4IbKv15YEB1Ej5Xs3t0M9yXGcRkJ6haKcfKH000PLXbua2"
        },
        {
            key: 'monthly',
            title: t('pricing.monthly.title'),
            price: t('pricing.monthly.price'),
            period: t('pricing.monthly.period'),
            description: t('pricing.monthly.description'),
            note: t('pricing.monthly.note'),
            badge: t('pricing.monthly.badge', { defaultValue: '' }) || null,
            highlight: true,
            stripeBtnId: "buy_btn_1SRfUEI1TlCT7peUektjBGQv",
            stripeBtnKey: "pk_live_51SP7cmI1TlCT7peUfpXils01Bpuh5LMIczy6dwh07c0yoQMPK6Rc4IbKv15YEB1Ej5Xs3t0M9yXGcRkJ6haKcfKH000PLXbua2"
        },
        {
            key: 'annual',
            title: t('pricing.annual.title'),
            price: t('pricing.annual.price'),
            period: t('pricing.annual.period'),
            description: t('pricing.annual.description'),
            note: t('pricing.annual.note'),
            badge: t('pricing.annual.badge', { defaultValue: '' }) || null,
            highlight: false,
            stripeBtnId: "buy_btn_1SRfPqI1TlCT7peUChTChKuF",
            stripeBtnKey: "pk_live_51SP7cmI1TlCT7peUfpXils01Bpuh5LMIczy6dwh07c0yoQMPK6Rc4IbKv15YEB1Ej5Xs3t0M9yXGcRkJ6haKcfKH000PLXbua2"
            //stripeBtnId: "buy_btn_1SP8VgRQpcNUzfssczFiain2",
            //stripeBtnKey: "pk_test_51SP7lFRQpcNUzfsslLcX1GRALaUUIBX0qNrWm8BKbENZqZJ9hlRoZyKnZxlEIuhn17GE0w3zz73Wvi63fXmgEXrF0006Mlz2gO"

        },
    ];

    return (
        <>
            <SEO
                title={t('pricing.seo.title')}
                description={t('pricing.seo.description')}
                url="/pricing"
            />
            <Layout showAuthLinks={true}>
                <div className="max-w-6xl mx-auto">
                    {/* Header Section */}
                    <div className="text-center mb-12">
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            {t('pricing.title')}
                        </h1>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            {t('pricing.subtitle')}
                        </p>
                    </div>

                    {/* Pricing Cards */}
                    <div className="grid gap-8 mb-12 md:grid-cols-2 lg:grid-cols-3">
                        {plans.map((plan) => {
                            const cardClasses = plan.highlight
                                ? 'relative border-2 border-primary-200 bg-gradient-to-br from-primary-50 via-white to-secondary-50 hover:border-primary-300 transition-all duration-300'
                                : 'relative border border-gray-200 hover:border-primary-200 transition-all duration-300';

                            const badgeClasses = plan.highlight
                                ? 'bg-primary-500 text-white'
                                : 'bg-gray-900 text-white';

                            return (
                                <Card key={plan.key} className={cardClasses} shadow="lg">
                                    {plan.badge && (
                                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                            <span className={`${badgeClasses} px-4 py-1 rounded-full text-sm font-semibold`}>
                                                {plan.badge}
                                            </span>
                                        </div>
                                    )}

                                    <Card.Header className="text-center mb-6">
                                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                            {plan.title}
                                        </h2>
                                        <div className="mb-4">
                                            <div className="flex items-baseline justify-center gap-2 mb-1">
                                                <span className="text-3xl font-bold text-gray-900">{plan.price}</span>
                                                <span className="text-base text-gray-600">{plan.period}</span>
                                            </div>
                                            {plan.note && (
                                                <p className="text-sm text-gray-500">{plan.note}</p>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-600">
                                            {plan.description}
                                        </p>
                                    </Card.Header>

                                    <Card.Content>
                                        <div className="space-y-3 mb-8">
                                            <h3 className="font-semibold text-gray-900 mb-3">
                                                {t('pricing.includes')}
                                            </h3>
                                            {sharedFeatures.map((feature, index) => (
                                                <div key={index} className="flex items-start gap-3">
                                                    <span className="text-lg flex-shrink-0">✓</span>
                                                    <span className="text-gray-700">{feature}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </Card.Content>

                                    <Card.Footer className="text-center">
                                        <stripe-buy-button
                                            buy-button-id={plan.stripeBtnId}
                                            publishable-key={plan.stripeBtnKey}
                                        >
                                        </stripe-buy-button>
                                    </Card.Footer>
                                </Card>
                            );
                        })}
                    </div>

                    {/* Guarantee Section */}
                    <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200" shadow="md">
                        <Card.Content className="text-center">
                            <div className="text-4xl mb-4">💸</div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">
                                {t('pricing.guarantee.title')}
                            </h3>
                            <p className="text-gray-700 max-w-2xl mx-auto">
                                {t('pricing.guarantee.description')}
                            </p>
                        </Card.Content>
                    </Card>

                    {/* Benefits Summary */}
                    <Card className="mt-8" shadow="md">
                        <Card.Content>
                            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                                {t('pricing.benefits.title')}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                                    <span className="text-2xl flex-shrink-0">⏱️</span>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-1">
                                            {t('landing.benefits.saveTime.title')}
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                            {t('landing.benefits.saveTime.description')}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                                    <span className="text-2xl flex-shrink-0">💰</span>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-1">
                                            {t('landing.benefits.saveMoney.title')}
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                            {t('landing.benefits.saveMoney.description')}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                                    <span className="text-2xl flex-shrink-0">🍃</span>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-1">
                                            {t('landing.benefits.saveFood.title')}
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                            {t('landing.benefits.saveFood.description')}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                                    <span className="text-2xl flex-shrink-0">📚</span>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-1">
                                            {t('landing.benefits.allRecipes.title')}
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                            {t('landing.benefits.allRecipes.description')}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                                    <span className="text-2xl flex-shrink-0">🎯</span>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-1">
                                            {t('landing.benefits.eatBetter.title')}
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                            {t('landing.benefits.eatBetter.description')}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                                    <span className="text-2xl flex-shrink-0">✏️</span>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-1">
                                            {t('landing.benefits.customizeRecipes.title')}
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                            {t('landing.benefits.customizeRecipes.description')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Card.Content>
                    </Card>

                    {/* Additional Info */}
                    <div className="mt-8 text-center text-sm text-gray-500">
                        <p>{t('pricing.footer')}</p>
                    </div>
                </div>
            </Layout>
        </>
    );
};

export default PricingPage;

